import { createReducer, isAnyOf } from '@reduxjs/toolkit';
import { AsyncStatus } from 'lib-shared-types-api';
import type { Organization } from 'lib-shared-types-api';
import { DeepWritable } from 'ts-essentials';

import { websocketActions } from 'middleware/websocket/actions/websocket';

import { appActions } from './app.actions';
import { AppError, AppLogInType } from './types/app.types';
import { ConversationTransferRequest } from './types/appConversationTransfer.types';
import { AppModal } from './types/appModal.types';

type AppState = {
  /** The async status. */
  asyncStatus: {
    /**
     * The status of the app. This status is one directional. Once it reaches
     * fulfilled status it can never go back.
     */
    app: AsyncStatus;
    /** The async status of the feature flags. */
    featureFlags: AsyncStatus;
    /** The async status of the presence. */
    presence: AsyncStatus;
    /** The async status of the server storage. */
    serverStorage: AsyncStatus;
  };
  /**
   * The modals.
   *
   * The first element in the array will be the one presented on the screen.
   * Upon dispatching the hide action for the first one, this gets removed and
   * the next in line becomes visible. This ensures that you can only have one
   * modal open at the time and that they queue up.
   */
  modals: AppModal['modal'][];
  /** The app error. */
  error?: AppError;
  /** The type of login that was used. */
  logInType?: AppLogInType;
  /** The ongoing conversation transfer requests. */
  conversationTransferRequests: ConversationTransferRequest[];
  /** The organizations that the user is a member of. */
  organizations: Organization[];
  /** The current organization. */
  organization?: Organization;
};

const initialState: AppState = {
  modals: [],
  asyncStatus: {
    app: 'uninitialized',
    featureFlags: 'uninitialized',
    presence: 'uninitialized',
    serverStorage: 'uninitialized',
  },
  conversationTransferRequests: [],
  organizations: [],
};

export const appReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(appActions.logIn, (state, action) => {
      state.logInType = action.payload.type;
    })
    .addCase(appActions.setError, (state, action) => {
      state.error = action.payload.error as DeepWritable<AppError>;
    })
    .addCase(appActions.showModal.action, (state, action) => {
      const modal = action.payload.modal as DeepWritable<
        Pick<AppModal, 'modal'>['modal']
      >;

      if (state.modals.every(({ type }) => type !== modal.type)) {
        state.modals = [modal, ...state.modals];
      }
    })
    .addCase(appActions.hideModal, (state, action) => {
      state.modals = state.modals.filter(
        (modal) => modal.type !== action.payload.type,
      );
    })
    .addCase(
      appActions.conversationTransferRequest.pending,
      (state, action) => {
        state.conversationTransferRequests.push(action.payload);
      },
    )
    .addCase(
      appActions.conversationTransferRequest.fulfilled,
      (state, action) => {
        const { type, csid } = action.payload.response.params;

        state.conversationTransferRequests =
          state.conversationTransferRequests.filter(
            ({ request }) => request.csid !== csid && request.type !== type,
          );
      },
    )
    .addCase(
      appActions.conversationTransferRequest.rejected,
      (state, action) => {
        const { csid, type } = action.payload.error;

        state.conversationTransferRequests =
          state.conversationTransferRequests.filter(
            ({ request }) => request.csid !== csid && request.type !== type,
          );
      },
    )
    .addCase(appActions.conversationTransferClear, (state, action) => {
      const { csid } = action.payload;

      state.conversationTransferRequests =
        state.conversationTransferRequests.filter(
          (transfer) => transfer.request.csid !== csid,
        );
    })
    .addCase(appActions.setOrganizations, (state, action) => {
      state.organizations = action.payload.organizations;
    })
    .addCase(appActions.setOrganization, (state, action) => {
      state.organization = action.payload.organization;
    })
    .addMatcher(
      isAnyOf(
        appActions.getFeatureFlags.pending,
        appActions.getFeatureFlags.fulfilled,
        appActions.getFeatureFlags.rejected,
      ),
      (state, action) => {
        state.asyncStatus.featureFlags = action.meta.requestStatus;
      },
    )
    .addMatcher(
      isAnyOf(
        appActions.getPresence.pending,
        appActions.getPresence.fulfilled,
        appActions.getPresence.rejected,
      ),
      (state, action) => {
        state.asyncStatus.presence = action.meta.requestStatus;
      },
    )
    .addMatcher(
      isAnyOf(
        appActions.getServerStorage.pending,
        appActions.getServerStorage.fulfilled,
        appActions.getServerStorage.rejected,
      ),
      (state, action) => {
        state.asyncStatus.serverStorage = action.meta.requestStatus;
      },
    )
    .addMatcher(
      isAnyOf(
        websocketActions.create,
        appActions.getFeatureFlags.pending,
        appActions.getServerStorage.pending,
      ),
      (state) => {
        if (state.asyncStatus.app !== 'fulfilled') {
          state.asyncStatus.app = 'pending';
        }
      },
    )
    .addMatcher(
      isAnyOf(
        websocketActions.createRejected,
        appActions.getFeatureFlags.rejected,
        appActions.getServerStorage.rejected,
      ),
      (state) => {
        if (state.asyncStatus.app !== 'fulfilled') {
          state.asyncStatus.app = 'rejected';
        }
      },
    )
    .addMatcher(
      isAnyOf(
        // We don't check that WebSocket is fulfilled as these actions only fire
        // when the WebSocket has been fulfilled.
        appActions.getFeatureFlags.fulfilled,
        appActions.getServerStorage.fulfilled,
      ),
      (state) => {
        if (
          state.asyncStatus.featureFlags === 'fulfilled' &&
          state.asyncStatus.serverStorage === 'fulfilled'
        ) {
          state.asyncStatus.app = 'fulfilled';
        }
      },
    );
});
